import { FpsSample } from 'src/utils/perf/types';

/**
 * rAF 기반 FPS 수집기.
 * 1초 간격의 프레임 카운트를 측정하여 FPS 샘플을 만든다.
 * document.hidden인 동안에는 측정을 일시중단한다.
 */
export class FpsCollector {
  private rafId: number | null = null;

  private frames = 0;

  private windowStart = 0;

  private listeners = new Set<(sample: FpsSample) => void>();

  private visibilityHandler: (() => void) | null = null;

  public start(): void {
    if (typeof window === 'undefined') return;
    this.visibilityHandler = () => {
      if (document.hidden) {
        this.stopRaf();
      } else {
        this.startRaf();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.startRaf();
  }

  public stop(): void {
    this.stopRaf();
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private stopRaf(): void {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private startRaf(): void {
    if (this.rafId != null || document.hidden) return;
    this.windowStart = performance.now();
    this.frames = 0;
    this.rafId = requestAnimationFrame(this.tick);
  }

  private tick = (timestamp: number): void => {
    this.rafId = null;
    if (document.hidden) return;

    this.frames += 1;
    const elapsed = timestamp - this.windowStart;
    if (elapsed >= 1000) {
      const fps = (this.frames * 1000) / elapsed;
      const sample: FpsSample = { fps: Math.round(fps * 10) / 10, timestamp: Date.now() };
      this.listeners.forEach((l) => l(sample));
      this.frames = 0;
      this.windowStart = timestamp;
    }
    this.rafId = requestAnimationFrame(this.tick);
  };

  public onSample(listener: (sample: FpsSample) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
